import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import type { ConnectorDetail, ConnectorTypeInfo } from '../lib/types';

// Field schema per connector type - just hints, all fields are free-form.
const FIELD_HINTS: Record<string, Array<{ key: string; label: string; type?: string; required?: boolean; help?: string; secret?: boolean }>> = {
  erpnext: [
    { key: 'base_url', label: 'ERPNext base URL', required: true, help: 'e.g. https://erp.example.com (no trailing slash)' },
    { key: 'api_key', label: 'API key', required: true, secret: true },
    { key: 'api_secret', label: 'API secret', required: true, secret: true },
    { key: 'upload_to_doctype', label: 'Upload PDFs to DocType (optional)', help: 'If set, generated PDFs are attached to this DocType via webhook' },
  ],
  salesforce: [
    { key: 'instance_url', label: 'Instance URL', required: true, help: 'e.g. https://myorg.my.salesforce.com' },
    { key: 'access_token', label: 'Access token (Bearer)', required: true, secret: true, help: 'OAuth Bearer token. Rotate manually or via your refresh flow.' },
    { key: 'client_id', label: 'Client ID (optional)' },
    { key: 'client_secret', label: 'Client secret (optional)', secret: true },
  ],
  generic: [
    { key: 'description', label: 'Description' },
    { key: 'test_url', label: 'Test URL (optional)', help: 'Used to verify the connector is reachable' },
    { key: 'test_method', label: 'Test method', type: 'select' },
    { key: 'test_headers_json', label: 'Test headers (JSON)', help: 'e.g. {"Authorization":"Bearer xxx"}' },
  ],
};

export default function ConnectorNew() {
  const nav = useNavigate();
  const [types, setTypes] = useState<ConnectorTypeInfo[]>([]);
  const [type, setType] = useState('erpnext');
  const [name, setName] = useState('');
  const [config, setConfig] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<ConnectorDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  useEffect(() => {
    api.listConnectorTypes().then(setTypes).catch((e) => setError((e as Error).message));
  }, []);

  useEffect(() => {
    // reset config when type changes
    setConfig({});
    setTestResult(null);
  }, [type]);

  function setField(k: string, v: string) {
    setConfig((c) => ({ ...c, [k]: v }));
  }

  async function createConnector() {
    setBusy(true);
    setError(null);
    try {
      // For generic, parse test_headers_json
      let parsedConfig: any = { ...config };
      if (type === 'generic' && config.test_headers_json) {
        try {
          parsedConfig.test_headers = JSON.parse(config.test_headers_json);
          delete parsedConfig.test_headers_json;
        } catch {
          throw new Error('Test headers JSON is invalid');
        }
      }
      const c = await api.createConnector({ name, type, config: parsedConfig });
      setCreated(c);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function runTest() {
    if (!created) return;
    setTestResult(null);
    try {
      const r = await api.testConnector(created.id);
      setTestResult(r);
    } catch (e) {
      setTestResult({ ok: false, message: (e as Error).message });
    }
  }

  if (created) {
    return (
      <div>
        <div className="alert success">
          Connector <strong>{created.name}</strong> created. Copy the values below now - they will not be shown again.
        </div>
        <div className="card">
          <h3>{created.name} <span className="pill info">{created.type}</span></h3>
          <dl className="kv">
            <dt>API key</dt>
            <dd>
              <code>{created.api_key}</code>
            </dd>
            <dt>Webhook secret</dt>
            <dd><code>{created.webhook_secret}</code></dd>
            <dt>Webhook URL pattern</dt>
            <dd><code>/api/v1/connectors/{created.id}/webhook/&#123;template_id&#125;</code></dd>
            <dt>POST /api/v1/generate</dt>
            <dd><code>Authorization: Bearer {created.api_key}</code></dd>
          </dl>
          <div className="row" style={{ marginTop: 16 }}>
            <button onClick={runTest}>Test connection</button>
            {testResult && (
              <span className={testResult.ok ? 'pill success' : 'pill danger'}>{testResult.message}</span>
            )}
            <button className="secondary" onClick={() => nav('/connectors')}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  const fields = FIELD_HINTS[type] || [];

  return (
    <div>
      <div className="card">
        <h3>New connector</h3>
        {error && <div className="alert error">{error}</div>}
        <div className="cols-2">
          <div className="field">
            <label>Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. erpnext-prod" />
          </div>
          <div className="field">
            <label>Type</label>
            <select value={type} onChange={(e) => setType(e.target.value)}>
              {types.map((t) => (
                <option key={t.type} value={t.type}>{t.displayName}</option>
              ))}
            </select>
          </div>
        </div>
        {types.find((t) => t.type === type) && (
          <p className="muted" style={{ marginTop: 0 }}>
            {types.find((t) => t.type === type)?.description}
          </p>
        )}
      </div>

      {fields.length > 0 && (
        <div className="card">
          <h3>Configuration</h3>
          {fields.map((f) => (
            <div className="field" key={f.key}>
              <label>
                {f.label} {f.required && <span style={{ color: 'var(--danger)' }}>*</span>}
              </label>
              {f.type === 'select' ? (
                <select value={config[f.key] || ''} onChange={(e) => setField(f.key, e.target.value)}>
                  <option value=""></option>
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                </select>
              ) : (
                <input
                  type={f.secret ? 'password' : 'text'}
                  value={config[f.key] || ''}
                  onChange={(e) => setField(f.key, e.target.value)}
                  placeholder={f.help}
                />
              )}
              {f.help && <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>{f.help}</div>}
            </div>
          ))}
        </div>
      )}

      <div className="card row">
        <button onClick={createConnector} disabled={!name || busy}>{busy ? 'Creating…' : 'Create connector'}</button>
        <button className="ghost" onClick={() => nav('/connectors')}>Cancel</button>
      </div>
    </div>
  );
}
